import { AsyncAction } from '../general/asyncAction';
import { ConnectionState, Disconnected } from '../general/connectionState';
import type { Center, CenterOpenStateNotification } from './center';
import type { FlightNetwork } from './flightNetwork';
import type { Launchpad, LaunchpadId } from './launchpad';
import type {
  Vehicle,
  VehicleId,
  VehicleStateNotification,
  VehicleMissionComputerStatusNotification,
  VehiclePreparationStatus
} from './vehicle';
import type { User } from './user';
import type {
  MissionCreatedNotification,
  MissionId,
  MissionDetailedStateNotification,
  MissionFlightDataNotification,
  MissionPreflightStateNotification,
  MissionRouteNotification,
  MissionFailureModeRouteInfo,
  MissionLockingInfoNotification,
  MissionEtasNotification,
  MissionTimestampsNotification,
  MissionRpicInControlNotification,
  MissionLogMessageNotification
} from './mission';
import type {
  AircraftId,
  ForeignAircraftDetailsNotification,
  ForeignAircraftRiskVolumeNotification
} from './foreignAircraft';
import type { PreflightOverviewNotification, PreflightMissionAtLaunchpadNotification } from './preflight';
import { UssConnectionState, type UssLogMessageNotification } from './uss';

/**
 * Holds all data for a single GCS connection.
 * This class is immutable - use the `with*` methods to create updated copies.
 */
export class GcsData {
  readonly connectionState: ConnectionState;
  readonly user: AsyncAction<User>;
  readonly vehicles: AsyncAction<readonly Vehicle[]>;
  readonly launchpads: AsyncAction<readonly Launchpad[]>;
  readonly flightNetwork: AsyncAction<FlightNetwork>;
  readonly center: AsyncAction<Center>;
  readonly centerOpenState: CenterOpenStateNotification | null;
  readonly vehiclePreparationStatusMap: Map<VehicleId, VehiclePreparationStatus>;
  readonly liveMissionsMap: Map<VehicleId, MissionCreatedNotification>;
  readonly missionPreflightOverviewMap: Map<MissionId, PreflightOverviewNotification>;
  readonly launchpadPreflightMissionMap: Map<LaunchpadId, PreflightMissionAtLaunchpadNotification>;
  readonly missionDetailedStateMap: Map<MissionId, MissionDetailedStateNotification>;
  readonly missionFlightDataMap: Map<MissionId, MissionFlightDataNotification>;
  readonly missionPreflightStateMap: Map<MissionId, MissionPreflightStateNotification>;
  readonly missionRouteMap: Map<MissionId, MissionRouteNotification>;
  readonly missionFailureModeRouteInfoMap: Map<MissionId, MissionFailureModeRouteInfo>;
  readonly missionLockingInfoMap: Map<MissionId, MissionLockingInfoNotification>;
  readonly missionEtasMap: Map<MissionId, MissionEtasNotification>;
  readonly missionTimestampsMap: Map<MissionId, MissionTimestampsNotification>;
  readonly missionRpicInControlMap: Map<MissionId, MissionRpicInControlNotification>;
  readonly missionLogMessagesMap: Map<MissionId, MissionLogMessageNotification[]>;
  readonly vehicleLaunchpadMap: Map<VehicleId, LaunchpadId>;
  readonly vehicleStateMap: Map<VehicleId, VehicleStateNotification>;
  readonly vehicleMissionComputerStatusMap: Map<VehicleId, VehicleMissionComputerStatusNotification>;
  readonly foreignAircraftDetailsMap: Map<AircraftId, ForeignAircraftDetailsNotification>;
  readonly foreignAircraftRiskVolumeMap: Map<AircraftId, ForeignAircraftRiskVolumeNotification>;
  readonly ussConnectionState: UssConnectionState | null;
  readonly ussLogs: UssLogMessageNotification[];

  constructor(
    connectionState: ConnectionState = new Disconnected(),
    user: AsyncAction<User> = AsyncAction.initial(),
    vehicles: AsyncAction<readonly Vehicle[]> = AsyncAction.initial(),
    launchpads: AsyncAction<readonly Launchpad[]> = AsyncAction.initial(),
    flightNetwork: AsyncAction<FlightNetwork> = AsyncAction.initial(),
    center: AsyncAction<Center> = AsyncAction.initial(),
    centerOpenState: CenterOpenStateNotification | null = null,
    vehiclePreparationStatusMap: Map<VehicleId, VehiclePreparationStatus> = new Map(),
    liveMissionsMap: Map<VehicleId, MissionCreatedNotification> = new Map(),
    missionPreflightOverviewMap: Map<MissionId, PreflightOverviewNotification> = new Map(),
    launchpadPreflightMissionMap: Map<LaunchpadId, PreflightMissionAtLaunchpadNotification> = new Map(),
    missionDetailedStateMap: Map<MissionId, MissionDetailedStateNotification> = new Map(),
    missionFlightDataMap: Map<MissionId, MissionFlightDataNotification> = new Map(),
    missionPreflightStateMap: Map<MissionId, MissionPreflightStateNotification> = new Map(),
    missionRouteMap: Map<MissionId, MissionRouteNotification> = new Map(),
    missionFailureModeRouteInfoMap: Map<MissionId, MissionFailureModeRouteInfo> = new Map(),
    missionLockingInfoMap: Map<MissionId, MissionLockingInfoNotification> = new Map(),
    missionEtasMap: Map<MissionId, MissionEtasNotification> = new Map(),
    missionTimestampsMap: Map<MissionId, MissionTimestampsNotification> = new Map(),
    missionRpicInControlMap: Map<MissionId, MissionRpicInControlNotification> = new Map(),
    missionLogMessagesMap: Map<MissionId, MissionLogMessageNotification[]> = new Map(),
    vehicleLaunchpadMap: Map<VehicleId, LaunchpadId> = new Map(),
    vehicleStateMap: Map<VehicleId, VehicleStateNotification> = new Map(),
    vehicleMissionComputerStatusMap: Map<VehicleId, VehicleMissionComputerStatusNotification> = new Map(),
    foreignAircraftDetailsMap: Map<AircraftId, ForeignAircraftDetailsNotification> = new Map(),
    foreignAircraftRiskVolumeMap: Map<AircraftId, ForeignAircraftRiskVolumeNotification> = new Map(),
    ussConnectionState: UssConnectionState | null = null,
    ussLogs: UssLogMessageNotification[] = []
  ) {
    this.connectionState = connectionState;
    this.user = user;
    this.vehicles = vehicles;
    this.launchpads = launchpads;
    this.flightNetwork = flightNetwork;
    this.center = center;
    this.centerOpenState = centerOpenState;
    this.vehiclePreparationStatusMap = vehiclePreparationStatusMap;
    this.liveMissionsMap = liveMissionsMap;
    this.missionPreflightOverviewMap = missionPreflightOverviewMap;
    this.launchpadPreflightMissionMap = launchpadPreflightMissionMap;
    this.missionDetailedStateMap = missionDetailedStateMap;
    this.missionFlightDataMap = missionFlightDataMap;
    this.missionPreflightStateMap = missionPreflightStateMap;
    this.missionRouteMap = missionRouteMap;
    this.missionFailureModeRouteInfoMap = missionFailureModeRouteInfoMap;
    this.missionLockingInfoMap = missionLockingInfoMap;
    this.missionEtasMap = missionEtasMap;
    this.missionTimestampsMap = missionTimestampsMap;
    this.missionRpicInControlMap = missionRpicInControlMap;
    this.missionLogMessagesMap = missionLogMessagesMap;
    this.vehicleLaunchpadMap = vehicleLaunchpadMap;
    this.vehicleStateMap = vehicleStateMap;
    this.vehicleMissionComputerStatusMap = vehicleMissionComputerStatusMap;
    this.foreignAircraftDetailsMap = foreignAircraftDetailsMap;
    this.foreignAircraftRiskVolumeMap = foreignAircraftRiskVolumeMap;
    this.ussConnectionState = ussConnectionState;
    this.ussLogs = ussLogs;
  }

  static initial(): GcsData {
    return new GcsData();
  }

  copyWith(
    updates: Partial<{
      connectionState: ConnectionState;
      user: AsyncAction<User>;
      vehicles: AsyncAction<readonly Vehicle[]>;
      launchpads: AsyncAction<readonly Launchpad[]>;
      flightNetwork: AsyncAction<FlightNetwork>;
      center: AsyncAction<Center>;
      centerOpenState: CenterOpenStateNotification | null;
      vehiclePreparationStatusMap: Map<VehicleId, VehiclePreparationStatus>;
      liveMissionsMap: Map<VehicleId, MissionCreatedNotification>;
      missionPreflightOverviewMap: Map<MissionId, PreflightOverviewNotification>;
      launchpadPreflightMissionMap: Map<LaunchpadId, PreflightMissionAtLaunchpadNotification>;
      missionDetailedStateMap: Map<MissionId, MissionDetailedStateNotification>;
      missionFlightDataMap: Map<MissionId, MissionFlightDataNotification>;
      missionPreflightStateMap: Map<MissionId, MissionPreflightStateNotification>;
      missionRouteMap: Map<MissionId, MissionRouteNotification>;
      missionFailureModeRouteInfoMap: Map<MissionId, MissionFailureModeRouteInfo>;
      missionLockingInfoMap: Map<MissionId, MissionLockingInfoNotification>;
      missionEtasMap: Map<MissionId, MissionEtasNotification>;
      missionTimestampsMap: Map<MissionId, MissionTimestampsNotification>;
      missionRpicInControlMap: Map<MissionId, MissionRpicInControlNotification>;
      missionLogMessagesMap: Map<MissionId, MissionLogMessageNotification[]>;
      vehicleLaunchpadMap: Map<VehicleId, LaunchpadId>;
      vehicleStateMap: Map<VehicleId, VehicleStateNotification>;
      vehicleMissionComputerStatusMap: Map<VehicleId, VehicleMissionComputerStatusNotification>;
      foreignAircraftDetailsMap: Map<AircraftId, ForeignAircraftDetailsNotification>;
      foreignAircraftRiskVolumeMap: Map<AircraftId, ForeignAircraftRiskVolumeNotification>;
      ussConnectionState: UssConnectionState;
      ussLogs: UssLogMessageNotification[];
    }>
  ): GcsData {
    return new GcsData(
      updates.connectionState ?? this.connectionState,
      updates.user ?? this.user,
      updates.vehicles ?? this.vehicles,
      updates.launchpads ?? this.launchpads,
      updates.flightNetwork ?? this.flightNetwork,
      updates.center ?? this.center,
      updates.centerOpenState ?? this.centerOpenState,
      updates.vehiclePreparationStatusMap ?? this.vehiclePreparationStatusMap,
      updates.liveMissionsMap ?? this.liveMissionsMap,
      updates.missionPreflightOverviewMap ?? this.missionPreflightOverviewMap,
      updates.launchpadPreflightMissionMap ?? this.launchpadPreflightMissionMap,
      updates.missionDetailedStateMap ?? this.missionDetailedStateMap,
      updates.missionFlightDataMap ?? this.missionFlightDataMap,
      updates.missionPreflightStateMap ?? this.missionPreflightStateMap,
      updates.missionRouteMap ?? this.missionRouteMap,
      updates.missionFailureModeRouteInfoMap ?? this.missionFailureModeRouteInfoMap,
      updates.missionLockingInfoMap ?? this.missionLockingInfoMap,
      updates.missionEtasMap ?? this.missionEtasMap,
      updates.missionTimestampsMap ?? this.missionTimestampsMap,
      updates.missionRpicInControlMap ?? this.missionRpicInControlMap,
      updates.missionLogMessagesMap ?? this.missionLogMessagesMap,
      updates.vehicleLaunchpadMap ?? this.vehicleLaunchpadMap,
      updates.vehicleStateMap ?? this.vehicleStateMap,
      updates.vehicleMissionComputerStatusMap ?? this.vehicleMissionComputerStatusMap,
      updates.foreignAircraftDetailsMap ?? this.foreignAircraftDetailsMap,
      updates.foreignAircraftRiskVolumeMap ?? this.foreignAircraftRiskVolumeMap,
      updates.ussConnectionState ?? this.ussConnectionState,
      updates.ussLogs ?? this.ussLogs
    );
  }

  withConnectionState(connectionState: ConnectionState): GcsData {
    return this.copyWith({ connectionState });
  }

  withUser(user: AsyncAction<User>): GcsData {
    return this.copyWith({ user });
  }

  withVehicles(vehicles: AsyncAction<readonly Vehicle[]>): GcsData {
    return this.copyWith({ vehicles });
  }

  withLaunchpads(launchpads: AsyncAction<readonly Launchpad[]>): GcsData {
    return this.copyWith({ launchpads });
  }

  withFlightNetwork(flightNetwork: AsyncAction<FlightNetwork>): GcsData {
    return this.copyWith({ flightNetwork });
  }

  withCenter(center: AsyncAction<Center>): GcsData {
    return this.copyWith({ center });
  }

  withCenterOpenState(centerOpenState: CenterOpenStateNotification): GcsData {
    return this.copyWith({ centerOpenState });
  }

  withVehiclePreparationStatusMap(vehiclePreparationStatusMap: Map<VehicleId, VehiclePreparationStatus>): GcsData {
    return this.copyWith({ vehiclePreparationStatusMap });
  }

  withLiveMissionsMap(liveMissionsMap: Map<VehicleId, MissionCreatedNotification>): GcsData {
    return this.copyWith({ liveMissionsMap });
  }

  withMissionPreflightOverviewMap(missionPreflightOverviewMap: Map<MissionId, PreflightOverviewNotification>): GcsData {
    return this.copyWith({ missionPreflightOverviewMap });
  }

  updateMissionPreflightOverview(overview: PreflightOverviewNotification): GcsData {
    const newOverviews = new Map(this.missionPreflightOverviewMap);
    newOverviews.set(overview.missionId, overview);
    return this.withMissionPreflightOverviewMap(newOverviews);
  }

  withLaunchpadPreflightMissionMap(
    launchpadPreflightMissionMap: Map<LaunchpadId, PreflightMissionAtLaunchpadNotification>
  ): GcsData {
    return this.copyWith({ launchpadPreflightMissionMap });
  }

  updateLaunchpadPreflightMission(notification: PreflightMissionAtLaunchpadNotification): GcsData {
    const newNotifications = new Map(this.launchpadPreflightMissionMap);
    if (notification.preflightInfo === null) {
      newNotifications.delete(notification.launchpadId);
    } else {
      newNotifications.set(notification.launchpadId, notification);
    }
    return this.withLaunchpadPreflightMissionMap(newNotifications);
  }

  addLiveMission(mission: MissionCreatedNotification): GcsData {
    const newMissions = new Map(this.liveMissionsMap);
    newMissions.set(mission.vehicleId, mission);
    return this.withLiveMissionsMap(newMissions);
  }

  removeLiveMission(missionId: MissionId): GcsData {
    const newMissions = new Map(this.liveMissionsMap);
    for (const [vehicleId, mission] of newMissions.entries()) {
      if (mission.missionId === missionId) {
        newMissions.delete(vehicleId);
        break;
      }
    }
    return this.withLiveMissionsMap(newMissions);
  }

  removeMissionData(missionId: MissionId): GcsData {
    const newPreflightOverviews = new Map(this.missionPreflightOverviewMap);
    newPreflightOverviews.delete(missionId);

    const newStates = new Map(this.missionDetailedStateMap);
    newStates.delete(missionId);

    const newFlightData = new Map(this.missionFlightDataMap);
    newFlightData.delete(missionId);

    const newPreflightStates = new Map(this.missionPreflightStateMap);
    newPreflightStates.delete(missionId);

    const newRoutes = new Map(this.missionRouteMap);
    newRoutes.delete(missionId);

    const newFailureModeRouteInfo = new Map(this.missionFailureModeRouteInfoMap);
    newFailureModeRouteInfo.delete(missionId);

    const newLockingInfo = new Map(this.missionLockingInfoMap);
    newLockingInfo.delete(missionId);

    const newEtas = new Map(this.missionEtasMap);
    newEtas.delete(missionId);

    const newTimestamps = new Map(this.missionTimestampsMap);
    newTimestamps.delete(missionId);

    const newRpicInControlMap = new Map(this.missionRpicInControlMap);
    newRpicInControlMap.delete(missionId);

    const newLogMessages = new Map(this.missionLogMessagesMap);
    newLogMessages.delete(missionId);

    return this.removeLiveMission(missionId)
      .withMissionPreflightOverviewMap(newPreflightOverviews)
      .withMissionDetailedStateMap(newStates)
      .withMissionFlightDataMap(newFlightData)
      .withMissionPreflightStateMap(newPreflightStates)
      .withMissionRouteMap(newRoutes)
      .withMissionFailureModeRouteInfoMap(newFailureModeRouteInfo)
      .withMissionLockingInfoMap(newLockingInfo)
      .withMissionEtasMap(newEtas)
      .withMissionTimestampsMap(newTimestamps)
      .withMissionRpicInControlMap(newRpicInControlMap)
      .withMissionLogMessagesMap(newLogMessages);
  }

  clearAllMissionData(): GcsData {
    return this.withLiveMissionsMap(new Map())
      .withMissionPreflightOverviewMap(new Map())
      .withMissionDetailedStateMap(new Map())
      .withMissionFlightDataMap(new Map())
      .withMissionPreflightStateMap(new Map())
      .withMissionRouteMap(new Map())
      .withMissionFailureModeRouteInfoMap(new Map())
      .withMissionLockingInfoMap(new Map())
      .withMissionEtasMap(new Map())
      .withMissionTimestampsMap(new Map())
      .withMissionRpicInControlMap(new Map())
      .withMissionLogMessagesMap(new Map());
  }

  withMissionDetailedStateMap(missionDetailedStateMap: Map<MissionId, MissionDetailedStateNotification>): GcsData {
    return this.copyWith({ missionDetailedStateMap });
  }

  updateMissionDetailedState(state: MissionDetailedStateNotification): GcsData {
    const newStates = new Map(this.missionDetailedStateMap);
    newStates.set(state.missionId, state);
    return this.withMissionDetailedStateMap(newStates);
  }

  withMissionFlightDataMap(missionFlightDataMap: Map<MissionId, MissionFlightDataNotification>): GcsData {
    return this.copyWith({ missionFlightDataMap });
  }

  updateMissionFlightData(flightData: MissionFlightDataNotification): GcsData {
    const newFlightData = new Map(this.missionFlightDataMap);
    newFlightData.set(flightData.missionId, flightData);
    return this.withMissionFlightDataMap(newFlightData);
  }

  withMissionPreflightStateMap(missionPreflightStateMap: Map<MissionId, MissionPreflightStateNotification>): GcsData {
    return this.copyWith({ missionPreflightStateMap });
  }

  updateMissionPreflightState(state: MissionPreflightStateNotification): GcsData {
    const newStates = new Map(this.missionPreflightStateMap);
    newStates.set(state.missionId, state);
    return this.withMissionPreflightStateMap(newStates);
  }

  withMissionRouteMap(missionRouteMap: Map<MissionId, MissionRouteNotification>): GcsData {
    return this.copyWith({ missionRouteMap });
  }

  updateMissionRoute(route: MissionRouteNotification): GcsData {
    const newRoutes = new Map(this.missionRouteMap);
    newRoutes.set(route.missionId, route);
    return this.withMissionRouteMap(newRoutes);
  }

  withMissionFailureModeRouteInfoMap(
    missionFailureModeRouteInfoMap: Map<MissionId, MissionFailureModeRouteInfo>
  ): GcsData {
    return this.copyWith({ missionFailureModeRouteInfoMap });
  }

  updateMissionFailureModeRouteInfo(failureModeRouteInfo: MissionFailureModeRouteInfo): GcsData {
    const newFailureModeRouteInfo = new Map(this.missionFailureModeRouteInfoMap);
    newFailureModeRouteInfo.set(failureModeRouteInfo.missionId, failureModeRouteInfo);
    return this.withMissionFailureModeRouteInfoMap(newFailureModeRouteInfo);
  }

  withMissionLockingInfoMap(missionLockingInfoMap: Map<MissionId, MissionLockingInfoNotification>): GcsData {
    return this.copyWith({ missionLockingInfoMap });
  }

  updateMissionLockingInfo(lockingInfo: MissionLockingInfoNotification): GcsData {
    const newLockingInfo = new Map(this.missionLockingInfoMap);
    newLockingInfo.set(lockingInfo.missionId, lockingInfo);
    return this.withMissionLockingInfoMap(newLockingInfo);
  }

  withMissionEtasMap(missionEtasMap: Map<MissionId, MissionEtasNotification>): GcsData {
    return this.copyWith({ missionEtasMap });
  }

  updateMissionEtas(etas: MissionEtasNotification): GcsData {
    const newEtas = new Map(this.missionEtasMap);
    newEtas.set(etas.missionId, etas);
    return this.withMissionEtasMap(newEtas);
  }

  withMissionTimestampsMap(missionTimestampsMap: Map<MissionId, MissionTimestampsNotification>): GcsData {
    return this.copyWith({ missionTimestampsMap });
  }

  updateMissionTimestamps(timestamps: MissionTimestampsNotification): GcsData {
    const newTimestamps = new Map(this.missionTimestampsMap);
    newTimestamps.set(timestamps.missionId, timestamps);
    return this.withMissionTimestampsMap(newTimestamps);
  }

  withMissionRpicInControlMap(missionRpicInControlMap: Map<MissionId, MissionRpicInControlNotification>): GcsData {
    return this.copyWith({ missionRpicInControlMap });
  }

  updateMissionRpicInControl(rpicInControl: MissionRpicInControlNotification): GcsData {
    const newRpicInControlMap = new Map(this.missionRpicInControlMap);
    newRpicInControlMap.set(rpicInControl.missionId, rpicInControl);
    return this.withMissionRpicInControlMap(newRpicInControlMap);
  }

  withMissionLogMessagesMap(missionLogMessagesMap: Map<MissionId, MissionLogMessageNotification[]>): GcsData {
    return this.copyWith({ missionLogMessagesMap });
  }

  addMissionLogMessage(logMessage: MissionLogMessageNotification): GcsData {
    const newLogMessages = new Map(this.missionLogMessagesMap);
    const existingMessages = newLogMessages.get(logMessage.missionId) || [];
    newLogMessages.set(logMessage.missionId, [...existingMessages, logMessage]);
    return this.withMissionLogMessagesMap(newLogMessages);
  }

  withVehicleLaunchpadMap(vehicleLaunchpadMap: Map<VehicleId, LaunchpadId>): GcsData {
    return this.copyWith({ vehicleLaunchpadMap });
  }

  withVehicleStateMap(vehicleStateMap: Map<VehicleId, VehicleStateNotification>): GcsData {
    return this.copyWith({ vehicleStateMap });
  }

  updateVehicleState(vehicleState: VehicleStateNotification): GcsData {
    const newStates = new Map(this.vehicleStateMap);
    newStates.set(vehicleState.id, vehicleState);
    return this.withVehicleStateMap(newStates);
  }

  withVehicleMissionComputerStatusMap(
    vehicleMissionComputerStatusMap: Map<VehicleId, VehicleMissionComputerStatusNotification>
  ): GcsData {
    return this.copyWith({ vehicleMissionComputerStatusMap });
  }

  updateVehicleMissionComputerStatus(status: VehicleMissionComputerStatusNotification): GcsData {
    const newStatuses = new Map(this.vehicleMissionComputerStatusMap);
    newStatuses.set(status.vehicleId, status);
    return this.withVehicleMissionComputerStatusMap(newStatuses);
  }

  withForeignAircraftDetailsMap(
    foreignAircraftDetailsMap: Map<AircraftId, ForeignAircraftDetailsNotification>
  ): GcsData {
    return this.copyWith({ foreignAircraftDetailsMap });
  }

  withForeignAircraftRiskVolumeMap(
    foreignAircraftRiskVolumeMap: Map<AircraftId, ForeignAircraftRiskVolumeNotification>
  ): GcsData {
    return this.copyWith({ foreignAircraftRiskVolumeMap });
  }

  updateForeignAircraftDetails(details: ForeignAircraftDetailsNotification): GcsData {
    const newDetails = new Map(this.foreignAircraftDetailsMap);
    newDetails.set(details.aircraftId, details);
    return this.withForeignAircraftDetailsMap(newDetails);
  }

  updateForeignAircraftRiskVolume(riskVolume: ForeignAircraftRiskVolumeNotification): GcsData {
    const newRiskVolumes = new Map(this.foreignAircraftRiskVolumeMap);
    newRiskVolumes.set(riskVolume.aircraftId, riskVolume);
    return this.withForeignAircraftRiskVolumeMap(newRiskVolumes);
  }

  removeForeignAircraftDetails(aircraftId: AircraftId): GcsData {
    const newDetails = new Map(this.foreignAircraftDetailsMap);
    newDetails.delete(aircraftId);
    return this.withForeignAircraftDetailsMap(newDetails);
  }

  removeForeignAircraftRiskVolume(aircraftId: AircraftId): GcsData {
    const newRiskVolumes = new Map(this.foreignAircraftRiskVolumeMap);
    newRiskVolumes.delete(aircraftId);
    return this.withForeignAircraftRiskVolumeMap(newRiskVolumes);
  }

  withUssConnectionState(ussConnectionState: UssConnectionState): GcsData {
    return this.copyWith({ ussConnectionState });
  }

  withUssLogs(ussLogs: UssLogMessageNotification[]): GcsData {
    return this.copyWith({ ussLogs });
  }

  addUssLogMessage(logMessage: UssLogMessageNotification): GcsData {
    const newLogs = [...this.ussLogs, logMessage];
    return this.withUssLogs(newLogs);
  }

  toDebugPayload(): Record<string, unknown> {
    const mapToObj = <K, V>(map: Map<K, V>): Record<string, V> =>
      Object.fromEntries([...map.entries()].map(([k, v]) => [String(k), v]));

    return {
      connectionState: { ...this.connectionState },
      user: { status: this.user.status, data: this.user.data, error: this.user.error },
      vehicles: { status: this.vehicles.status, data: this.vehicles.data },
      launchpads: { status: this.launchpads.status, data: this.launchpads.data },
      flightNetwork: { status: this.flightNetwork.status, data: this.flightNetwork.data },
      center: { status: this.center.status, data: this.center.data },
      centerOpenState: this.centerOpenState,
      vehiclePreparationStatusMap: mapToObj(this.vehiclePreparationStatusMap),
      liveMissionsMap: mapToObj(this.liveMissionsMap),
      missionPreflightOverviewMap: mapToObj(this.missionPreflightOverviewMap),
      launchpadPreflightMissionMap: mapToObj(this.launchpadPreflightMissionMap),
      missionDetailedStateMap: mapToObj(this.missionDetailedStateMap),
      missionFlightDataMap: mapToObj(this.missionFlightDataMap),
      missionPreflightStateMap: mapToObj(this.missionPreflightStateMap),
      missionRouteMap: Object.fromEntries(
        [...this.missionRouteMap.entries()].map(([id, r]) => [String(id), { nodeCount: r.route.length }])
      ),
      missionFailureModeRouteInfoMap: mapToObj(this.missionFailureModeRouteInfoMap),
      missionLockingInfoMap: mapToObj(this.missionLockingInfoMap),
      missionEtasMap: mapToObj(this.missionEtasMap),
      missionTimestampsMap: mapToObj(this.missionTimestampsMap),
      missionRpicInControlMap: mapToObj(this.missionRpicInControlMap),
      missionLogMessagesMap: Object.fromEntries(
        [...this.missionLogMessagesMap.entries()].map(([id, logs]) => [String(id), { logCount: logs.length }])
      ),
      vehicleLaunchpadMap: mapToObj(this.vehicleLaunchpadMap),
      vehicleStateMap: mapToObj(this.vehicleStateMap),
      vehicleMissionComputerStatusMap: mapToObj(this.vehicleMissionComputerStatusMap),
      foreignAircraftDetailsMap: mapToObj(this.foreignAircraftDetailsMap),
      foreignAircraftRiskVolumeMap: mapToObj(this.foreignAircraftRiskVolumeMap),
      ussConnectionState: this.ussConnectionState,
      ussLogCount: this.ussLogs.length
    };
  }
}
